import React from 'react';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { ClassRequestWithStudent } from '@/types/classRequests';
import { useI18n } from '@/contexts/I18nContext';

interface IndividualRequestCardProps {
  request: ClassRequestWithStudent;
  onAccept: (requestId: string) => void;
  onDeny: (requestId: string) => void;
  isAccepting?: boolean;
  isDenying?: boolean;
}

export const IndividualRequestCard: React.FC<IndividualRequestCardProps> = ({
  request,
  onAccept,
  onDeny,
  isAccepting = false,
  isDenying = false
}) => {
  const { t } = useI18n();
  
  // Create a test ID based on student name (normalized for use in data-testid)
  const studentNameForTestId = request.student_name.toLowerCase().replace(/\s+/g, '-');
  const testId = `request-card-${studentNameForTestId}-${request.request_id.slice(0, 8)}`;
  
  return (
    <div className="rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FramedAvatar 
            src={request.student_avatar_url}
            initials={request.student_name.split(' ').map((n, index) => n[0]).join('').toUpperCase()}
            size={32}
            frameSize={{ w: 40, h: 44 }}
            beltRank={request.student_belt_rank || 'white'}
            showFrame={true}
          />
          <div>
            <p className="text-white font-bold text-[12px]">
              {request.student_name}
            </p>
            {request.student_academy && request.student_academy.trim() !== '' && (
              <p className="text-white/60 text-[10px]">
                {request.student_academy}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onAccept(request.request_id)}
            disabled={isAccepting}
            data-testid={`accept-button-${studentNameForTestId}`}
            className="bg-sossiety-red text-white text-[10px] font-bold px-3 py-1 rounded-full hover:bg-sossiety-red-hover transition-colors disabled:opacity-50"
          >
            {isAccepting ? '...' : t('request_card.approve')}
          </button>
          <button
            onClick={() => onDeny(request.request_id)}
            disabled={isDenying}
            className="border border-white text-white text-[10px] font-bold px-3 py-1 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            {isDenying ? '...' : t('request_card.deny')}
          </button>
        </div>
      </div>
    </div>
  );
};
